import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { DopMenuService } from 'projects/dop-menu/src/lib/dop-menu.service';
import { AuthConfigService } from './auth-config.service';

@Injectable()
export class DopAuthService {

  private authConfig: AuthConfig;
  private readonly forceLoginPromptKey = 'force_login_prompt_once';
  private hasSetupAutomaticRefresh = false;

  constructor(
    private authConfigService: AuthConfigService,
    private oauthService: OAuthService,
    private dopMenuService: DopMenuService,
    private httpClient: HttpClient) {
  }


  async login(configurationPath: string, internalFileName: string) {
    await this.setConfiguration(configurationPath);
    if (internalFileName !== '') {
      window.localStorage.setItem('internalFileName', internalFileName);
    } else if (!window.location.hash.includes('access_token') && window.localStorage.getItem('internalFileName') != null) {
      window.localStorage.removeItem('internalFileName');
    }
    await this.ensureDiscoveryDocumentLoaded();
    await this.oauthService.tryLogin()
      .then(async () => {
        if (!this.authConfigService.isEntraAuthProvider()) {
          this.dopMenuService.setupSessionValidator();
        }
        if (!this.oauthService.hasValidAccessToken()) {
          if (this.authConfigService.isEntraAuthProvider()) {
            const tokenRefreshed = await this.tryRefreshEntraToken();
            if (tokenRefreshed) {
              return true;
            }

            // Entra: Use authorization code flow with optional prompt
            if (this.consumeForceLoginPrompt()) {
              this.oauthService.initLoginFlow('', { prompt: 'login' });
            } else {
              this.oauthService.initLoginFlow();
            }
          } else {
            // FAMS: Use implicit flow (original behavior)
            this.oauthService.initImplicitFlow();
          }
        }
        else
        {
          if (!this.authConfigService.isEntraAuthProvider()) {
            this.validateFamsSession();
          }
        }
        return true;
      });
  }

  private validateFamsSession(): void {
    const sessionId = this.getSessionId();
    if (sessionId === null) {
      return;
    }

    const queryApiBaseUrl = this.authConfigService.fullConfigurations?.apiConfig?.orderQueryBaseUrl;
    if (!queryApiBaseUrl) {
      return;
    }

    const url = queryApiBaseUrl + 'authentication/session/' + sessionId + '/status';

    this.httpClient.get(url).subscribe((data: any) => {
      if (data !== null && data.isRevoked) {
        this.oauthService.logOut();
        window.location.reload();
      }
      else {
        return true;
      }
    });
  }

  private getSessionId(): string {
    const idClaims = this.oauthService.getIdentityClaims();
    type ObjectKey = keyof typeof idClaims;
    if (idClaims === null || idClaims === undefined) {
        return null;
    }
    return idClaims['pi.sri' as ObjectKey];
  }

  private async setConfiguration(configurationPath: string) {
    await this.authConfigService.setConfiguration(configurationPath);
    this.authConfig = this.authConfigService.setupAuthConfig();
    this.oauthService.configure(this.authConfig);

    if (this.authConfigService.isEntraAuthProvider() && !this.hasSetupAutomaticRefresh) {
      // Trigger refresh only for access token expiry to avoid duplicate refresh calls.
      this.oauthService.setupAutomaticSilentRefresh({}, 'access_token');
      this.hasSetupAutomaticRefresh = true;
    }
  }

  private async tryRefreshEntraToken(): Promise<boolean> {
    if (!this.authConfigService.isEntraAuthProvider()) {
      return false;
    }

    if (!this.oauthService.hasValidIdToken()) {
      return false;
    }

    const refreshToken = this.oauthService.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      await this.oauthService.refreshToken();
      return this.oauthService.hasValidAccessToken();
    } catch (error) {
      console.warn('Entra token refresh failed. Falling back to login flow.', error);
      return false;
    }
  }

  private async ensureDiscoveryDocumentLoaded(): Promise<void> {
    if (!this.authConfigService.isEntraAuthProvider() || this.oauthService.logoutUrl) {
      return;
    }

    try {
      await this.oauthService.loadDiscoveryDocument();
    } catch (error) {
      // Keep app startup alive while logging a detailed auth diagnostics entry.
      console.error('Failed to load OIDC discovery document.', error);
    }
  }

  private consumeForceLoginPrompt(): boolean {
    const shouldForcePrompt = window.sessionStorage.getItem(this.forceLoginPromptKey) === 'true';
    if (shouldForcePrompt) {
      window.sessionStorage.removeItem(this.forceLoginPromptKey);
    }

    return shouldForcePrompt;
  }

  get postLogoutRedirectUrl() {
    return this.oauthService.postLogoutRedirectUri;
  }

  get redirectUrl(): string {
    return this.oauthService.redirectUri;
  }

  get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  get idToken(): string {
    return this.oauthService.getIdToken();
  }

  get claims() {
    return this.oauthService.getIdentityClaims();
  }

  get isLoggedIn() {
    return this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
 }

  async logout(): Promise<void> {
    await this.ensureDiscoveryDocumentLoaded();
    this.oauthService.logOut();
  }

}
